import { setMinimalPromiseTime } from 'common/utils/setMinimalPromiseTime';
import { fetchSimProductsWithConfiguration } from '../SimProductsApi/simProductsApi';
import { simProductsQueries } from '../Inventory/queries/simProductsQueries';
import { SimListResponseData } from 'src/common/components/Sim/Inventory/types/simListTypes';
import { useValidatedSearchId } from 'src/common/components/Filters/hooks/useValidatedSearchId';
import { getSearchIds } from 'src/common/components/Sim/Filters/SimIdSearchInput.util';
import { useAtomValue } from 'jotai';
import {
  pageFilterAtom,
  rowsPerPageFilterAtom,
  simProductsFiltersAtom,
  simProductsSortingAtomWithStorage,
} from 'atoms/filters/sim-products.ts';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useExtendsSimProductsWithActionsAvailability } from 'products/sim/Actions/hooks/useExtendsSimProductsWithActionsAvailability.ts';
import { useMemo } from 'react';

export const useSimProductsFetch = () => {
  const rowsPerPage = useAtomValue(rowsPerPageFilterAtom);
  const page = useAtomValue(pageFilterAtom);

  const filters = useAtomValue(simProductsFiltersAtom);

  const sorting = useAtomValue(simProductsSortingAtomWithStorage);

  const { tags, searchText, chosenIdType, customFields, ...otherFilters } = filters;

  const { search } = useValidatedSearchId(searchText);

  const queryKey = simProductsQueries.list({
    searchText: search,
    chosenIdType,
    page,
    rowsPerPage,
    ...otherFilters,
    tags,
    sorting,
    customFields,
  });

  const simProductQuery = useSuspenseQuery<SimListResponseData>({
    queryKey: queryKey,
    queryFn: ({ signal }) => {
      return setMinimalPromiseTime(
        fetchSimProductsWithConfiguration(
          page,
          rowsPerPage,
          {
            searchText: getSearchIds(search),
            chosenIdType: chosenIdType,
            tags,
            customFields,
            ...otherFilters,
          },
          sorting,
          signal,
        ),
      );
    },
    refetchOnWindowFocus: false,
  });

  const extendedSimProducts = useExtendsSimProductsWithActionsAvailability(
    simProductQuery.data.items,
  );

  return useMemo(
    () => ({
      ...simProductQuery,
      data: {
        ...simProductQuery.data,
        items: extendedSimProducts,
      },
    }),
    [simProductQuery, extendedSimProducts],
  );
};
